import {from} from 'rxjs';
import {map, mergeMap, toArray} from 'rxjs/operators';
import {downloadWorkbookWithFrozenRows} from '../../../../common/upload/excel.utils';
import {AppContext} from '../../../../app-context';
import {Entity} from '../../../../handlers/entity';
import {isUploadSourceMeter} from './upload-access';
import {canUploadConnection, createUploadOverview, kindLabels, uploadChannels, uploadKind, uploadKinds} from './upload-workbook';
import {UploadWorkbookComponent} from './upload-workbook.component';
import {ChangeDetectorRef, Component, ElementRef, inject, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {View} from '../../../../common/view';
import {Handler} from '../../../../common/handler';
import {TranslateDirective} from '../../../../common/utils/translate.directive';
import {AppCommonUtils} from '../../../../common/app-common-utils';
import {DownloadableFile} from '@flowmaps-com/flowmaps-typescriptmodels';
import {periodActionPayload, UploadHistoryScope, UploadPeriodDetails, UploadPeriodRow, uploadStatus} from './upload-history.types';

@Component({selector: 'app-upload-history', standalone: true, imports: [CommonModule, FormsModule, UploadWorkbookComponent], host: {class: 'upload-history notranslate'},
  templateUrl: './upload-history.component.html', styleUrls: ['./upload-history.component.scss']})
@Handler()
export class UploadHistoryComponent extends View implements OnInit, OnDestroy {
  @Input() data: UploadHistoryScope & {preferredConnectionId?: string;
    pendingUpload?: {file: File}} = {};
  year = new Date().getFullYear();
  rows: UploadPeriodRow[] = [];
  details?: UploadPeriodDetails;
  detailsLoading = false;
  detailsError = '';
  @ViewChild('detailsDialog', {static: true}) detailsDialog: ElementRef<HTMLDialogElement>;
  private changeDetector = inject(ChangeDetectorRef);
  private detailsReturnFocus?: HTMLElement;
  loading = false;
  moreAvailable = false;
  error = '';
  busy = false;
  confirmation?: {row: UploadPeriodRow; action: 'DELETE' | 'RESTORE'};
  private requestRevision = 0;
  private detailsRevision = 0;
  private alive = true;
  historyOffset = 0;

  connections: Entity[] = [];
  excludedKinds = new Set<string>();
  @ViewChild(UploadWorkbookComponent, {static: true}) workbook: UploadWorkbookComponent;
  private pendingUploadOpened = false;
  get visibleRows() { return this.rows.filter(row => !this.excludedKinds.has(uploadKind(row.dataType))); }
  get selectionOptions() {
    const available = new Set<string>(this.rows.map(row => uploadKind(row.dataType)));
    this.connections.forEach(entity => uploadChannels(entity).forEach(type => available.add(uploadKind(type))));
    return uploadKinds.filter(kind => available.has(kind));
  }
  get selectedCount() { return this.selectionOptions.filter(kind => !this.excludedKinds.has(kind)).length; }
  get canAddUpload() { return this.connections.some(entity => canUploadConnection(entity, AppContext.userProfile)); }
  kindName(kind: string) { return this.t(kindLabels[kind]); }
  toggleKind(kind: string, checked: boolean) {
    this.excludedKinds = new Set(this.excludedKinds);
    checked ? this.excludedKinds.delete(kind) : this.excludedKinds.add(kind);
  }
  selectAllKinds(checked: boolean) { this.excludedKinds = new Set(checked ? [] : this.selectionOptions); }
  ngOnInit() { this.data.sourceId ? this.load() : this.initialise(); }
  initialise() {
    this.loading = true;
    this.sendQuery('getConnectionsAsEntities', {}, {caching: false, showSpinner: false, hideError: true}).subscribe({
      next: (entities: Entity[]) => {
        if (!this.alive) return;
        this.connections = entities.filter(entity => (!this.data.organisationId || entity.organisation.organisationId === this.data.organisationId)
          && (!this.data.locationId || entity.location.locationId === this.data.locationId)
          && (!this.data.connectionId || entity.connection.connectionId === this.data.connectionId)
          && (this.data.connectionIds == null || this.data.connectionIds.includes(entity.connection.connectionId)));
        this.load();
        if (this.data.pendingUpload && !this.pendingUploadOpened) {
          this.pendingUploadOpened = true;
          void this.workbook.openExisting(this.data.pendingUpload.file);
        }
      }, error: () => {if (this.alive) {this.loading = false; this.error = this.t('Upload history could not be loaded');}}
    });
  }
  openWorkbook(mode: 'template' | 'upload') { this.workbook.open(mode, Number(this.year), this.data.preferredConnectionId); }
  uploaded(year: number) { this.year = year; this.initialise(); }
  exportOverview() {
    const book = createUploadOverview(this.visibleRows, Number(this.year), key => this.t(key));
    downloadWorkbookWithFrozenRows(book, 'flowmaps-uploads-'+this.year+'.xlsx',
      new Map([[book.SheetNames[0], 3]]));
  }
  ngOnDestroy() {
    this.alive = false; this.requestRevision++; this.detailsRevision++;
    this.detailsDialog.nativeElement.close(); this.onDestroy();
  }
  t(key: string): string { return TranslateDirective.getTranslation(key.toLowerCase(), key) as string; }
  status(value: string) { return this.t(uploadStatus(value)); }
  overviewStatus(row: UploadPeriodRow) {
    return this.t(row.deleted ? 'Deleted' : row.legacy ? 'Existing data' : 'Available');
  }
  calendarPeriod(month: string) {
    const start = new Date(month+'-01T12:00:00');
    const end = new Date(start.getFullYear(), start.getMonth()+1, 0, 12);
    const format = (value: Date) => value.toLocaleDateString(this.locale());
    return format(start)+' – '+format(end);
  }
  channel(value: string) {
    return this.t(({electricityConsumption: 'Electricity consumption', electricityFeedIn: 'Electricity feed-in',
      heatConsumption: 'Heat consumption', coolingConsumption: 'Cooling consumption', gasConsumption: 'Gas consumption',
      waterConsumption: 'Water consumption', chargePointConsumption: 'Charge point consumption'})[value] || value);
  }
  locale() { return AppCommonUtils.getPreferredLanguage() === 'nl' ? 'nl-NL' : 'en-GB'; }
  number(value: number | string) { return Number(value).toLocaleString(this.locale(), {maximumFractionDigits: 6}); }
  month(value: string) { return new Date(value+'-01T12:00:00').toLocaleDateString(this.locale(), {month: 'long', year: 'numeric'}); }
  date(value?: string) { return value ? new Date(value).toLocaleString(this.locale(), {dateStyle: 'short', timeStyle: 'short'}) : '—'; }
  trackRow(index: number, row: UploadPeriodRow) { return row.periodId; }

  load() {
    const revision = ++this.requestRevision;
    this.detailsRevision++;
    this.closeDetails();
    this.loading = true; this.error = ''; this.details = undefined; this.confirmation = undefined;
    const {preferredConnectionId, pendingUpload, ...queryScope} = this.data;
    const scope = {...queryScope, year: Number(this.year)};
    // A location is bounded by its visible manual meters. Reading each source/year also includes legacy periods
    // and avoids truncating later sources at the general overview's 100-period limit.
    const sources = this.data.locationId && !this.data.sourceId ? [...new Set(this.connections.flatMap(entity =>
      (entity.connection.meters || []).filter(isUploadSourceMeter).map(meter => meter.meterId)))] : [];
    const query = (sourceId?: string) => this.sendQuery('com.flowmaps.api.measurements.uploads.GetUploadPeriods',
      sourceId ? {...scope, sourceId} : scope, {caching: false, showSpinner: false, hideError: true});
    const requests = sources.length ? from(sources).pipe(mergeMap(sourceId => query(sourceId), 4), toArray(), map(pages => ({
      periods: [...new Map(pages.flatMap(page => page.periods).map(row => [row.periodId, row])).values()]
        .sort((a: UploadPeriodRow, b: UploadPeriodRow) => b.month.localeCompare(a.month) || a.connectionCode.localeCompare(b.connectionCode) || a.dataType.localeCompare(b.dataType)),
      moreAvailable: pages.some(page => page.moreAvailable)
    }))) : query();
    requests.subscribe({
      next: result => {
        if (!this.alive || revision !== this.requestRevision) return;
        this.rows = result.periods; this.moreAvailable = result.moreAvailable; this.loading = false;
      },
      error: () => { if (this.alive && revision === this.requestRevision) {this.loading = false; this.error = this.t('Upload history could not be loaded');} }
    });
  }
  showDetails(row: UploadPeriodRow, offset = 0, trigger?: EventTarget | null) {
    this.confirmation = undefined;
    const revision = ++this.detailsRevision;
    this.details = {period: row, history: [], historyCount: 0};
    this.historyOffset = offset; this.detailsError = ''; this.detailsLoading = !row.legacy;
    if (!this.detailsDialog.nativeElement.open) {
      this.detailsReturnFocus = trigger instanceof HTMLElement ? trigger : document.activeElement as HTMLElement;
      this.changeDetector.detectChanges();
      this.detailsDialog.nativeElement.showModal();
    }
    if (row.legacy) return;
    this.sendQuery('com.flowmaps.api.measurements.uploads.GetUploadPeriodDetails', {periodId: row.periodId, offset},
      {caching: false, showSpinner: false, hideError: true}).subscribe({
      next: result => {
        if (this.alive && revision === this.detailsRevision) {this.details = result; this.detailsLoading = false;}
      },
      error: () => {
        if (this.alive && revision === this.detailsRevision) {
          this.detailsLoading = false; this.detailsError = this.t('Upload details could not be loaded');
        }
      }
    });
  }
  closeDetails(event?: Event) {
    event?.preventDefault();
    if (this.busy) return;
    this.detailsRevision++;
    this.detailsDialog?.nativeElement.close();
    this.details = undefined; this.detailsLoading = false; this.detailsError = ''; this.confirmation = undefined;
    if (this.detailsReturnFocus?.isConnected) this.detailsReturnFocus.focus({preventScroll: true});
    this.detailsReturnFocus = undefined;
  }
  ask(row: UploadPeriodRow, action: 'DELETE' | 'RESTORE') {
    if (!this.detailsDialog.nativeElement.open || this.details?.period.periodId !== row.periodId) {
      this.showDetails(row);
    }
    this.confirmation = {row, action};
    setTimeout(() => {
      if (!this.alive) return;
      const panel = this.elementRef.nativeElement.querySelector('.confirmation') as HTMLElement;
      panel?.scrollIntoView({block: 'nearest'}); panel?.focus({preventScroll: true});
    });
  }
  apply() {
    if (!this.confirmation || this.busy) return;
    const {row, action} = this.confirmation;
    const payload = periodActionPayload(row, action);
    this.busy = true;
    this.sendCommand('com.flowmaps.api.measurements.uploads.ChangeUploadedPeriod', payload, updated => {
      this.busy = false; this.confirmation = undefined;
      this.rows = this.rows.map(p => p.periodId === updated.periodId ? updated : p);
      this.showDetails(updated);
      AppCommonUtils.clearCache();
      this.waitForProjection(updated, action);
    }, error => {
      this.busy = false; this.confirmation = undefined; this.load(); AppCommonUtils.registerError(error);
    }, {eventOnSuccess: false});
  }
  private waitForProjection(row: UploadPeriodRow, action: 'DELETE' | 'RESTORE', attempt = 0) {
    this.busy = true;
    this.sendQuery('com.flowmaps.api.measurements.uploads.GetUploadPeriodReady', {periodId: row.periodId, revision: row.revision},
      {caching: false, showSpinner: false, hideError: true}).subscribe({next: ready => {
        if (!this.alive) return;
        if (ready === true) {
          this.sendCommand('com.flowmaps.api.measurements.uploads.NotifyUploadedMeasurementsReady',
            {periods:[{periodId:row.periodId,revision:row.revision}]},()=>{
              this.busy=false;AppCommonUtils.clearCache();
              AppCommonUtils.registerSuccess(this.t(action === 'DELETE' ? 'Upload period deleted' : 'Upload period restored'));
            },()=>{this.busy=false;this.error=this.t('The change is saved. Measurements are still being updated; refresh shortly.');},
            {eventOnSuccess:false});
        } else if (attempt < 7) {
          setTimeout(() => {if (this.alive) this.waitForProjection(row, action, attempt+1);}, Math.min(1000, 200*(attempt+1)));
        } else {
          this.busy = false; this.error = this.t('The change is saved. Measurements are still being updated; refresh shortly.');
        }
      }, error: () => {this.busy = false; this.error = this.t('The change is saved. Measurements are still being updated; refresh shortly.');}});
  }
  download(archiveId: string) {
    this.sendQuery('com.flowmaps.api.measurements.uploads.GetUploadedSourceFile', {archiveId},
      {caching: false, showSpinner: true}).subscribe((file: DownloadableFile) => {
        const bytes = Uint8Array.from(atob(file.base64Data), c => c.charCodeAt(0));
        const url = URL.createObjectURL(new Blob([bytes], {type: 'application/octet-stream'}));
        const link = document.createElement('a'); link.href = url;
        link.download = file.fileName.split(/[\/]/).pop(); link.click(); URL.revokeObjectURL(url);
      });
  }
}
